import { Component } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map } from "lodash"
import { CardContent, Paper, Tooltip as MuiTooltip } from "@mui/material"
import { OfflineBolt } from "@mui/icons-material"
import { PERIOD, DATE, RESOURCE_STATUS } from "helpers/shapes"
import AnalyzeHeader from "modules/analyze-header"
import MultiLine from "components/charts/multi-line"
import MultiLineOverlapping from "components/charts/multi-line-overlapping"
import Tooltip from "components/analyze-tooltip"
import { numberFormatter, oneDecimalPercentageFormatter, autoFormatter, smallPercentageFormatter } from "helpers/formatters/numbers"
import NoData from "components/no-data"
import NoBetas from "components/no-betas"
import AnalyzeBetaDetails from "modules/analyze-beta-details"
import AnalyzeConcentrationDetails from "modules/analyze-concentration-details"
import { betasAreMissing } from "selectors/missingDataFiles"
import BreakdownTooltip from "components/breakdown-tooltip"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import LoadingWrapper from "components/loading-wrapper"
import { computeCSVData } from "helpers/csv"
import "./style.scss"

export default class AnalyzeComposition extends Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    portfolioId: PropTypes.string.isRequired,
    base: PropTypes.object,
    earliestDate: DATE,
    latestDate: DATE,
    startDate: DATE,
    aiSummaryStartDate: DATE,
    endDate: DATE,
    selectedDate: DATE,
    period: PERIOD.isRequired,
    data: PropTypes.array.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
    })).isRequired,
    displayType: PropTypes.string.isRequired,
    viewType: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    setPeriod: PropTypes.func.isRequired,
    setDate: PropTypes.func.isRequired,
    setDisplayType: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    trackCSVDownload: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    drift: PropTypes.object.isRequired,
    focusedChartLine: PropTypes.string,
    fileName: PropTypes.string.isRequired,
    firstDate: PropTypes.string.isRequired,
    resetDate: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })).isRequired,
    selectedOption: PropTypes.object.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    only: PropTypes.string,
    displayTypeName: PropTypes.string.isRequired,
    filtersVisible: PropTypes.bool.isRequired,
    missingDataFiles: PropTypes.object.isRequired,
    modelId: PropTypes.string.isRequired,
    refEquityOverride: PropTypes.bool,
    datesAdjusted: PropTypes.bool.isRequired,
    betaInstrument: PropTypes.object,
    currency: PropTypes.string,
    passthroughResourcesStatus: RESOURCE_STATUS.isRequired,
  }

  static ACTIVE_TOOLTIP_MSG = `
    Although a comparison is currently provided,
    this portfolio's concentration is displayed in absolute terms.`

  componentDidUpdate() {
    const { firstDate, startDate: propsStartDate, selectedDate, resetDate } = this.props
    const startDate = firstDate || propsStartDate

    if (selectedDate && (selectedDate < startDate)) {
      resetDate()
    }
  }

  onTabChanged = (_, id) => {
    const { setDisplayType } = this.props
    setDisplayType(id)
  }

  onToggleOnly = (factorId) => {
    const { toggleOnly } = this.props
    toggleOnly(factorId)
  }

  render() {
    const {
      earliestDate,
      latestDate,
      setDate,
      selectedDate,
      startDate,
      aiSummaryStartDate,
      endDate,
      period,
      datesAdjusted,
      data,
      items,
      displayType,
      options,
      toggle,
      focusChartLine,
      blurChartLine,
      focusedChartLine,
      viewType,
      isActive,
      drift,
      fileName,
      resetDate,
      firstDate,
      selectedOption,
      trackCSVDownload,
      only,
      account,
      portfolioId,
      base,
      setPeriod,
      addNotification,
      displayTypeName,
      missingDataFiles,
      modelId,
      refEquityOverride,
      betaInstrument,
      currency,
      passthroughResourcesStatus,
    } = this.props

    let graph
    let rightPanel

    if (!earliestDate || !latestDate) {
      graph = <NoData title={displayTypeName} className="graph" />
    }

    if (displayType === "beta") {
      if (betasAreMissing(missingDataFiles, modelId)) {
        graph = <NoBetas modelId={modelId} />
      } else {
        graph = <MultiLine
          tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={numberFormatter} />}
          {...{
            startDate,
            date: selectedDate,
            setDate,
            endDate,
            data,
            onlyItem: only,
            items,
            toggle,
            focusChartLine,
            blurChartLine,
            focusedChartLine,
            toggleOnly: this.onToggleOnly,
          }}
        />
        rightPanel = <AnalyzeBetaDetails
          selectedDate={selectedDate}
          viewType={viewType}
          displayType={displayType}
          displayTypeName={displayTypeName}
          startDate={firstDate}
          endDate={endDate}
          drift={drift}
          selectedOption={selectedOption}
          betaInstrument={betaInstrument}
        />
      }
    } else {
      graph = <MultiLineOverlapping
        tooltip={<BreakdownTooltip
          data={data}
          items={items}
          totalKey="positionsCount"
          formatters={{
            maxConcentration: smallPercentageFormatter,
            topConcentration: smallPercentageFormatter,
            positionsCount: (value) => value,
          }}
        />}
        {...{
          totalTickFormat: autoFormatter,
          tickFormat: oneDecimalPercentageFormatter,
          startDate,
          date: selectedDate,
          setDate,
          endDate,
          data,
          onlyItem: only,
          totalLabel: "Positions",
          breakdownLabel: `Concentration (% of ${refEquityOverride ? "Ref Equity" : "GMV"})`,
          items,
          toggle: (...args) => toggle([...args, displayType]),
          focusChartLine,
          blurChartLine,
          focusedChartLine,
          toggleOnly: this.onToggleOnly,
        }}
      />
      rightPanel = <AnalyzeConcentrationDetails
        selectedDate={selectedDate}
        viewType={viewType}
        displayType={displayType}
        displayTypeName={displayTypeName}
        startDate={firstDate}
        endDate={endDate}
        drift={drift}
        selectedOption={selectedOption}
      />
    }

    return <div className="modules_analyze-composition">
      <div className="composition-wrapper">
        <AnalyzeHeader
          selectedId="composition"
          {...{
            account,
            adjusted: datesAdjusted,
            portfolioId,
            base,
            earliestDate,
            latestDate,
            startDate,
            aiSummaryStartDate,
            endDate,
            selectedDate,
            period,
            setPeriod,
            addNotification,
            resetDate,
            firstDate,
            isActive,
            currency,
            changeDisplayPreferenceType: "navigation.analyze.trends",
            showFilterScope: true,
          }}
        />
        <CardContent className={classnames("card-content")}>
          <Paper className="content">
            <div className="graph">
              <GraphTopBar {...{
                value: displayType,
                onTabChanged: this.onTabChanged,
                tabs: map(options, ({ id, label }) => ({
                  key: id,
                  value: id,
                  label,
                  tooltip: isActive && id === "concentration"
                    ? <MuiTooltip title={AnalyzeComposition.ACTIVE_TOOLTIP_MSG}>
                      <OfflineBolt className="warning" />
                    </MuiTooltip> : null,
                })),
                actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items)} fileName={fileName} track={trackCSVDownload} />],
              }}
              />
              <LoadingWrapper status={[passthroughResourcesStatus]}>{graph}</LoadingWrapper>
            </div>
            {rightPanel}
          </Paper>
        </CardContent>
      </div>
    </div>
  }
}
